import { HashMap } from '@ngneat/transloco';
import { CONFIRM_SIGN_UP_SUCCESS } from '@platform-frontend/account';
import {
  ABSTRACT_NOT_FOUND_NOTIFICATION,
  ABSTRACT_OUTDATED_NOTIFICATION,
  AUTHORIZATION_REFUSED_NOTIFICATION,
  INVALID_VAT_ID_NOTIFICATION,
  REDIRECT_ID_NOT_KNOWN_NOTIFICATION,
  SINGLE_PRICE_UPDATED_NOTIFICATION,
  SubscriptionIntervalParams,
  VAT_ID_NOT_MATCHING_COUNTRY_NOTIFICATION,
  VAT_ID_VERIFICATION_FAILED_NOTIFICATION,
  WRONG_PRICING_INFORMATION_NOTIFICATION
} from '@platform-frontend/marketplace';
import {
  MSG_PLATFORM_MAX_REQUESTS_EXCEEDED,
  NEW_EMAIL_FAILED_NOTIFICATION,
  NEW_EMAIL_FAILED_SERVER_UNREACHABLE_NOTIFICATION,
  NEW_EMAIL_SENT_NOTIFICATION,
  VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION,
  YATTA_SUPPORT_EMAIL_ADDRESS
} from '@platform-frontend/platform';
import { PriceParams, buildNotificationTranslationId } from '@platform-frontend/transloco';
import { TranslationId, buildMailToLink, buildNotificationActionLink } from '@platform-frontend/util';
import { ResendAcademicVerification } from './academic/academic.actions';
import { CHECKOUT_TRANSLOCO_SCOPE } from './checkout-transloco-scope';
import { GotoFlowStart } from './flow/flow.actions';
import { RequestRefreshPricing } from './shopping-cart/shopping-cart.actions';

const buildTranslationId: (key: string, params?: HashMap) => TranslationId = (key: string, params: HashMap = {}) =>
  buildNotificationTranslationId(key, params, CHECKOUT_TRANSLOCO_SCOPE);

// Shared messages
const ACADEMIC_MISSING_STATE_DATA_NOTIFICATION = buildTranslationId('shared.academicMissingStateData', {
  restartRegistrationLink: buildNotificationActionLink(new GotoFlowStart(), undefined, '54e0cf2c-019c-468f-bc54-30e004952d03')
});
const SELECT_INSTANCE_FAILED_NOTIFICATION = (instanceName: 'billingAddress' | 'paymentMethod'): TranslationId =>
  buildTranslationId('shared.selectInstanceFailed', { instanceName });
const VAT_ID_VERIFICATION_FAILED_NOTIFICATION_WITH_ACTION = buildTranslationId('shared.vatIdVerificationFailed', {
  refreshPricingLink: buildNotificationActionLink(new RequestRefreshPricing(), undefined, 'de5f6c69-0efc-4309-9f42-7576894da4e2')
});
const UPDATED_PAYMENT_METHOD_NOTIFICATION = buildTranslationId('shared.updatedPaymentMethod');
const DELETE_CONNECTED_WARNING = (instanceName: 'billingAddress' | 'paymentMethod'): TranslationId =>
  buildTranslationId('shared.deleteConnectedWarning', { instanceName });
const ACADEMIC_VERIFICATION_SUCCESSFULL_BUT_CONFIRMATION_PAGE_NOT_SHOWN = buildTranslationId(
  'shared.academicVerificationSuccessfullButConfirmationPageNotShown'
);
const QUANTITY_CONSTRAINTS_VIOLATION = buildTranslationId('shared.quantityConstraintsViolation');
const BILLING_INFORMATION_REQUIRED_MISSING = buildTranslationId('shared.billingInformationRequiredMissing');
const BILLING_INFORMATION_REQUIRED_NOT_SELECTED = buildTranslationId('shared.billingInformationRequiredNotSelected');

// Confirm Sign Up
export const MSG_ACADEMIC_CONFIRM_SIGN_UP_SUCCESS = CONFIRM_SIGN_UP_SUCCESS('academic');
export const MSG_COMMERCIAL_CHECKOUT_SIGN_UP_SUCCESS = CONFIRM_SIGN_UP_SUCCESS('checkout');

// Academic program
export const MSG_ACADEMIC_SET_LICENSE_SCOPE_MISSING_STATE_DATA = ACADEMIC_MISSING_STATE_DATA_NOTIFICATION;
export const MSG_ACADEMIC_SET_IP_RANGE_MISSING_STATE_DATA = ACADEMIC_MISSING_STATE_DATA_NOTIFICATION;
export const MSG_ACADEMIC_REGISTRATION_MISSING_STATE_DATA = ACADEMIC_MISSING_STATE_DATA_NOTIFICATION;
export const MSG_ACADEMIC_REGISTRATION_FAILED_LOADING_STUDENT_ID = buildTranslationId('academicRegistration.failedLoadingStudentId');
export const MSG_ACADEMIC_REGISTRATION_DEFAULT = buildTranslationId('academicRegistration.default.message', {
  supportMailLink: buildMailToLink(
    YATTA_SUPPORT_EMAIL_ADDRESS,
    buildTranslationId('academicRegistration.default.mailToYattaSupport.subject'),
    null,
    '582982b0-bee8-4b84-b080-d75be6922fa5'
  )
});
export const MSG_ACADEMIC_VERIFICATION_DEFAULT = VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION;
export const MSG_ACADEMIC_VERIFICATION_RESEND_SUCCESS = NEW_EMAIL_SENT_NOTIFICATION;
export const MSG_ACADEMIC_VERIFICATION_RESEND_MAX_MAIL_REQUESTS_EXCEEDED = MSG_PLATFORM_MAX_REQUESTS_EXCEEDED;
export const MSG_ACADEMIC_VERIFICATION_RESEND_SERVER_UNREACHABLE = NEW_EMAIL_FAILED_SERVER_UNREACHABLE_NOTIFICATION(new ResendAcademicVerification());
export const MSG_ACADEMIC_VERIFICATION_RESEND_DEFAULT = NEW_EMAIL_FAILED_NOTIFICATION(new ResendAcademicVerification());
export const MSG_ACADEMIC_VERIFICATION_COMPLETION_FAILED = ACADEMIC_VERIFICATION_SUCCESSFULL_BUT_CONFIRMATION_PAGE_NOT_SHOWN;
const CONFIRM_ACADEMIC_LINK_INVALID_NOTIFICATION = buildTranslationId('confirmAcademicEmail.linkInvalid');
export const MSG_CONFIRM_ACADEMIC_EMAIL_USER_NOT_KNOWN = CONFIRM_ACADEMIC_LINK_INVALID_NOTIFICATION;
export const MSG_CONFIRM_ACADEMIC_EMAIL_CONFIRM_ID_NOT_MATCHING_EMAIL = CONFIRM_ACADEMIC_LINK_INVALID_NOTIFICATION;
export const MSG_CONFIRM_ACADEMIC_EMAIL_CONFIRM_ID_EXPIRED = buildTranslationId('confirmAcademicEmail.confirmIdExpired');
export const MSG_SHOW_CONFIRMATION_PAGE_AFTER_CONFIRM_ACADEMIC_EMAIL_FAILED = ACADEMIC_VERIFICATION_SUCCESSFULL_BUT_CONFIRMATION_PAGE_NOT_SHOWN;

// Billing address
export const MSG_SELECT_BILLING_ADDRESS_DEFAULT = SELECT_INSTANCE_FAILED_NOTIFICATION('billingAddress');
export const MSG_CONNECT_BILLING_ADDRESS_WRONG_PRICING_INFORMATION = WRONG_PRICING_INFORMATION_NOTIFICATION;
export const MSG_CONNECT_BILLING_ADDRESS_INVALID_VAT_ID = INVALID_VAT_ID_NOTIFICATION;
export const MSG_CONNECT_BILLING_ADDRESS_VAT_SERVICE_NOT_REACHABLE = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_CONNECT_BILLING_ADDRESS_COUNTRY_INVALID = VAT_ID_NOT_MATCHING_COUNTRY_NOTIFICATION;
export const MSG_CONNECT_BILLING_ADDRESS_INTERNAL_VAT_CALCULATION_ERROR = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_CONNECT_BILLING_ADDRESS_NOT_FOUND = buildTranslationId('connectBillingAddress.notFound');
export const MSG_CONNECT_BILLING_ADDRESS_DEFAULT = SELECT_INSTANCE_FAILED_NOTIFICATION('billingAddress');
export const MSG_CONNECT_BILLING_ADDRESS_SUCCESS = buildTranslationId('connectBillingAddress.success');
export const MSG_CONNECT_BILLING_ADDRESS_BILLING_INFORMATION_REQUIRED_MISSING = BILLING_INFORMATION_REQUIRED_MISSING;
export const MSG_CONNECT_BILLING_ADDRESS_BILLING_INFORMATION_REQUIRED_NOT_SELECTED = BILLING_INFORMATION_REQUIRED_NOT_SELECTED;
export const MSG_DELETE_CONNECTED_BILLING_ADDRESS_WARNING = DELETE_CONNECTED_WARNING('billingAddress');

// Payment method
const IBAN_SERVICE_ERROR_NOTIFICATION = buildTranslationId('ibanDetails.default');
export const MSG_IBAN_DETAILS_IBAN_SERVICE_NOT_REACHABLE = IBAN_SERVICE_ERROR_NOTIFICATION;
export const MSG_IBAN_DETAILS_DEFAULT = IBAN_SERVICE_ERROR_NOTIFICATION;
export const MSG_UPDATE_PAYMENT_METHOD_SUCCESS = UPDATED_PAYMENT_METHOD_NOTIFICATION;
export const MSG_SELECT_PAYMENT_METHOD_DEFAULT = SELECT_INSTANCE_FAILED_NOTIFICATION('paymentMethod');
export const MSG_CONNECT_PAYMENT_METHOD_DEFAULT = SELECT_INSTANCE_FAILED_NOTIFICATION('paymentMethod');
export const MSG_CONNECT_PAYMENT_REFRESHING_PAYMENT_METHODS_FAILED = buildTranslationId('connectPaymentMethod.refreshingPaymentMethodsFailed');
export const MSG_CONNECT_PAYMENT_METHOD_SUCCESS = UPDATED_PAYMENT_METHOD_NOTIFICATION;
export const MSG_CONNECT_PAYMENT_METHOD_PREVENT_SCA = buildTranslationId('connectPaymentMethod.preventSca');
export const MSG_CONNECT_REDIRECT_ID_NOT_KNOWN = REDIRECT_ID_NOT_KNOWN_NOTIFICATION;
export const MSG_CONNECT_AUTHORIZATION_REFUSED = AUTHORIZATION_REFUSED_NOTIFICATION;
export const MSG_DELETE_CONNECTED_PAYMENT_METHOD_WARNING = DELETE_CONNECTED_WARNING('paymentMethod');

// Fetch pricing information during commercial checkout
export const MSG_CHECKOUT_PRICING_INVALID_VAT_ID = INVALID_VAT_ID_NOTIFICATION;
export const MSG_CHECKOUT_PRICING_VAT_SERVICE_NOT_REACHABLE = VAT_ID_VERIFICATION_FAILED_NOTIFICATION_WITH_ACTION;
export const MSG_CHECKOUT_PRICING_COUNTRY_INVALID = VAT_ID_NOT_MATCHING_COUNTRY_NOTIFICATION;
export const MSG_CHECKOUT_PRICING_INTERNAL_VAT_CALCULATION_ERROR = VAT_ID_VERIFICATION_FAILED_NOTIFICATION_WITH_ACTION;
export const MSG_CHECKOUT_PRICING_QUANTITY_CONSTRAINTS_VIOLATION = QUANTITY_CONSTRAINTS_VIOLATION;
export const MSG_CHECKOUT_PRICING_DEFAULT = buildTranslationId('checkoutPricing.default', {
  refreshPricingLink: buildNotificationActionLink(new RequestRefreshPricing(), undefined, 'd501a81a-a573-43f5-935b-eb9a4397b927')
});

// Purchase or Renew
const PURCHASE_OR_RENEW_DEFAULT = (licenseKindType: 'subscription' | 'purchase'): TranslationId =>
  buildTranslationId('purchaseOrRenew.default', { licenseKindType });
export const MSG_PURCHASE_ALREADY_HAVE_ACTIVE_SUBSCRIPTION = buildTranslationId('purchase.alreadyHaveActiveSubscription');
export const MSG_PURCHASE_ALREADY_HAVE_CANCELED_SUBSCRIPTION = buildTranslationId('purchase.alreadyHaveCanceledSubscription');
export const MSG_RENEW_ALREADY_HAVE_ACTIVE_SUBSCRIPTION = buildTranslationId('renew.alreadyHaveActiveSubscription');
export const MSG_RENEW_SUBSCRIPTION_EXPIRED = buildTranslationId('renew.subscriptionExpired');
export const MSG_PURCHASE_OR_RENEW_CVC_DECLINED = buildTranslationId('purchaseOrRenew.cvcDeclined');
export const MSG_PURCHASE_OR_RENEW_CARD_EXPIRED = buildTranslationId('purchaseOrRenew.cardExpired');
export const MSG_PURCHASE_OR_RENEW_EXPIRY_DATE_INVALID = buildTranslationId('purchaseOrRenew.expiryDateInvalid');
export const MSG_PURCHASE_OR_RENEW_INVALID_CARD_NUMBER = buildTranslationId('purchaseOrRenew.invalidCardNumber');
export const MSG_PURCHASE_OR_RENEW_INVALID_IBAN = buildTranslationId('purchaseOrRenew.invalidIban');
export const MSG_PURCHASE_OR_RENEW_FRAUD_DETECTED = buildTranslationId('purchaseOrRenew.fraudDetected.message', {
  supportMailLink: buildMailToLink(
    YATTA_SUPPORT_EMAIL_ADDRESS,
    buildTranslationId('purchaseOrRenew.fraudDetected.mailToYattaSupport.subject'),
    null,
    'd5c359f8-b138-4e51-a827-7b2dab92a609'
  )
});
const PAYMENT_TOKEN_ERROR_NOTIFICATION = (errorType: 'expired' | 'invalid'): TranslationId =>
  buildTranslationId('purchaseOrRenew.paymentTokenError', { errorType });
export const MSG_PURCHASE_OR_RENEW_ENCRYPTED_DATA_EXPIRED = PAYMENT_TOKEN_ERROR_NOTIFICATION('expired');
export const MSG_PURCHASE_OR_RENEW_INVALID_ENCRYPTED_DATA = PAYMENT_TOKEN_ERROR_NOTIFICATION('invalid');
export const MSG_PURCHASE_OR_RENEW_INVALID_VAT_ID = INVALID_VAT_ID_NOTIFICATION;
export const MSG_PURCHASE_OR_RENEW_VAT_SERVICE_NOT_REACHABLE = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_PURCHASE_OR_RENEW_COUNTRY_INVALID = VAT_ID_NOT_MATCHING_COUNTRY_NOTIFICATION;
export const MSG_PURCHASE_OR_RENEW_INTERNAL_VAT_CALCULATION_ERROR = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_NOT_FOUND = ABSTRACT_NOT_FOUND_NOTIFICATION('billingInformation');
export const MSG_PURCHASE_OR_RENEW_METADATA_ALREADY_USED = (licenseKind: 'subscription' | 'purchase'): TranslationId =>
  buildTranslationId('purchaseOrRenew.metadataAlreadyUsed', { licenseKind });
export const MSG_PURCHASE_OR_RENEW_PAYMENT_METHOD_NOT_FOUND = ABSTRACT_NOT_FOUND_NOTIFICATION('paymentMethod');
export const MSG_PURCHASE_OR_RENEW_PAYMENT_SUCCESS_BUT_ERROR = buildTranslationId('purchaseOrRenew.paymentSuccessButError');
export const MSG_PURCHASE_OR_RENEW_PAYPAL_PAYMENT_REFUSED = buildTranslationId('purchaseOrRenew.paypalPaymentRefused');
export const MSG_PURCHASE_OR_RENEW_NOT_ENOUGH_BALANCE = buildTranslationId('purchaseOrRenew.notEnoughBalance');
export const MSG_PURCHASE_OR_RENEW_SUBSCRIPTION_DEFAULT = PURCHASE_OR_RENEW_DEFAULT('subscription');
export const MSG_PURCHASE_OR_RENEW_DEFAULT = PURCHASE_OR_RENEW_DEFAULT('purchase');
export const MSG_PURCHASE_OR_RENEW_NO_PAYMENT_METHOD = buildTranslationId('purchaseOrRenew.noPaymentMethod');
export const MSG_PURCHASE_OR_RENEW_NO_PRICING_INFORMATION = buildTranslationId('purchaseOrRenew.noPricingInformation');
export const MSG_PURCHASE_OR_RENEW_NO_QUANTITY = buildTranslationId('purchaseOrRenew.noQuantity');
export const MSG_PURCHASE_OR_RENEW_NO_ADDITIONS = buildTranslationId('purchaseOrRenew.noAdditions');
export const MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_REQUIRED_MISSING = BILLING_INFORMATION_REQUIRED_MISSING;
export const MSG_PURCHASE_OR_RENEW_BILLING_INFORMATION_REQUIRED_NOT_SELECTED = BILLING_INFORMATION_REQUIRED_NOT_SELECTED;
export const MSG_PURCHASE_OR_RENEW_REJECTED_PAYMENT = buildTranslationId('purchaseOrRenew.rejectedPayment.message', {
  supportMailLink: buildMailToLink(
    YATTA_SUPPORT_EMAIL_ADDRESS,
    buildTranslationId('purchaseOrRenew.rejectedPayment.mailToYattaSupport.subject'),
    null,
    '7bf55fb3-591c-4399-85b4-61a467986e64'
  )
});
export const MSG_PURCHASE_OR_RENEW_PAGE_LOADING = buildTranslationId('purchaseOrRenew.pageLoading');
export const MSG_PURCHASE_OR_RENEW_PRICE_LOADING = buildTranslationId('purchaseOrRenew.priceLoading');
export const MSG_PURCHASE_OR_RENEW_OUTDATED_PAYMENT_INFORMATION_VERSION = ABSTRACT_OUTDATED_NOTIFICATION('paymentMethod');
export const MSG_PURCHASE_OR_RENEW_OUTDATED_BILLING_INFORMATION_VERSION = ABSTRACT_OUTDATED_NOTIFICATION('billingInformation');
export const MSG_PURCHASE_OR_RENEW_REDIRECT_ID_NOT_KNOWN = REDIRECT_ID_NOT_KNOWN_NOTIFICATION;
export const MSG_PURCHASE_OR_RENEW_AUTHORIZATION_REFUSED = AUTHORIZATION_REFUSED_NOTIFICATION;
export const MSG_PURCHASE_OR_RENEW_NOT_ENOUGH_BUDGET = buildTranslationId('purchaseOrRenew.notEnoughBudget');
export const MSG_PURCHASE_OR_RENEW_WRONG_PRICING = buildTranslationId('purchaseOrRenew.wrongPricing');
export const MSG_PURCHASE_OR_RENEW_QUANTITY_CONSTRAINTS_VIOLATION = QUANTITY_CONSTRAINTS_VIOLATION;
export const MSG_RENEW_NO_CONTRACT_ID = buildTranslationId('renew.noContractId');

// SelectLicenseType
export const MSG_SELECT_LICENSE_TYPE_VALIDITY_PERIOD_INFO = (remainingDays: number): TranslationId =>
  buildTranslationId('selectLicenseType.validityPeriodInfo', { remainingDays });

// Fetch pricing information during subscription management
export const MSG_SUBMAN_PRICING_INVALID_VAT_ID = INVALID_VAT_ID_NOTIFICATION;
export const MSG_SUBMAN_PRICING_VAT_SERVICE_NOT_REACHABLE = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_SUBMAN_PRICING_COUNTRY_INVALID = VAT_ID_NOT_MATCHING_COUNTRY_NOTIFICATION;
export const MSG_SUBMAN_PRICING_INTERNAL_VAT_CALCULATION_ERROR = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_SUBMAN_PRICING_DEFAULT = WRONG_PRICING_INFORMATION_NOTIFICATION;

// Price change due update of billing address
export const MSG_CURRENCY_CHANGE_ON_BILLING_ADDRESS_UPDATE = buildTranslationId('onBillingAddressUpdate.currencyChange');
export const MSG_PRICE_CHANGE_ON_BILLING_ADDRESS_UPDATE = buildTranslationId('onBillingAddressUpdate.priceChange');
export const MSG_PRICE_UNCHANGED_ON_BILLING_ADDRESS_UPDATE = buildTranslationId('onBillingAddressUpdate.priceUnchanged');

// Flow
const SWITCH_FLOW_FROM_PURCHASE_NOTIFICATION = (target: 'subscriptionManagement' | 'purchaseInformation'): TranslationId =>
  buildTranslationId('switchFlow.fromPurchaseTo', { target });
export const MSG_SWITCH_FLOW_FROM_PURCHASE_TO_SUBMAN = SWITCH_FLOW_FROM_PURCHASE_NOTIFICATION('subscriptionManagement');
export const MSG_SWITCH_FLOW_FROM_PURCHASE_TO_PURCHINFO = SWITCH_FLOW_FROM_PURCHASE_NOTIFICATION('purchaseInformation');
export const MSG_SWITCH_FLOW_FROM_SUBMAN_TO_PURCHASE = buildTranslationId('switchFlow.fromSubmanToPurchase');
export const MSG_SWITCH_FLOW_FROM_SUBMAN_TO_PURCHASE_EXPIRING = buildTranslationId('switchFlow.fromSubmanToPurchaseExpiring');
export const MSG_STARTUP_PURCHASE_WITH_ASSETS = (quantity: number, productName: string): TranslationId =>
  buildTranslationId('startupPurchaseWithAssets', { quantity, productName });
export const MSG_STARTUP_FLOW_PURCHASE_NOT_ALLOWED = buildTranslationId('startupFlow.purchaseNotAllowed');
export const MSG_STARTUP_FLOW_PURCHASE_NOT_ACCESSIBLE = buildTranslationId('startupFlow.purchaseNotAccessible');
export const MSG_STARTUP_FLOW_SUBSCRIPTION_NOT_ACCESSIBLE = buildTranslationId('startupFlow.subscriptionNotAccessible');

// Update belonged products
export const MSG_UPDATE_BELONGED_SOLUTIONS_DEFAULT = buildTranslationId('updateBelongedSolutions.default');

// Personalization
export const MSG_SET_FIRST_PERSON_DEFAULT = buildTranslationId('setFirstPerson.default');

// Price updates
export const MSG_PRICE_UPDATES_SINGLE_INCREASE = (
  product: string,
  oldPrice: PriceParams,
  newPrice: PriceParams,
  period: SubscriptionIntervalParams
): TranslationId => SINGLE_PRICE_UPDATED_NOTIFICATION('increase', product, oldPrice, newPrice, period);

// Invoices
export const MSG_DOWNLOAD_LATEST_INVOICE_DEFAULT = buildTranslationId('downloadLatestInvoice.default');

// Close window
export const MSG_CLOSE_WINDOW_DEFAULT = buildTranslationId('closeWindow.default');

// WalletPayment
export const MSG_WALLET_PAYMENT_APPLE_DEFAULT = buildTranslationId('walletPayment.apple.default');
export const MSG_WALLET_PAYMENT_GOOGLE_DEFAULT = buildTranslationId('walletPayment.google.default');
export const MSG_WALLET_PAYMENT_GOOGLE_ACCOUNT_ERROR = buildTranslationId('walletPayment.google.accountError');
